import { ArrowLeft } from 'lucide-react';

export interface Hierarchy {
  dbName: string;
  company: string;
  area: string;
}

interface HierarchyBreadcrumbProps {
  /** Database › Company › Area — the ancestors above the machine. Null until /machines/{id} resolves. */
  hierarchy: Hierarchy | null;
  machineName: string;
  /** When set, the machine segment de-emphasises and the point becomes the leaf. */
  pointName?: string;
  onBack: () => void;
  /** Optional trailing status dot (point page). Full tailwind bg-* class. */
  statusColor?: string;
}

/**
 * Sticky hierarchy bar shown at the top of the machine page and the point page.
 *
 * Single source of truth for both — they previously diverged: the machine page
 * carried a breadcrumb whose point segment was unreachable dead code (PointDetail
 * is an early return, so the machine header never rendered with a point selected),
 * and the point page carried no hierarchy at all.
 *
 * Sticks to the top of <main className="flex-1 overflow-y-auto p-6"> in App.tsx.
 * The -mx-6/px-6 pair cancels that container's horizontal padding so the bar and
 * its bottom border run edge to edge.
 *
 * Ancestor segments truncate rather than wrap: a wrapping sticky bar changes
 * height mid-scroll and shifts the page under the reader. The leaf never truncates.
 */
export function HierarchyBreadcrumb({
  hierarchy,
  machineName,
  pointName,
  onBack,
  statusColor,
}: HierarchyBreadcrumbProps) {
  const ancestors = hierarchy
    ? [hierarchy.dbName, hierarchy.company, hierarchy.area].filter(Boolean)
    : [];

  // With a point selected the machine drops back to being an ancestor.
  if (pointName && machineName) ancestors.push(machineName);
  const leaf = pointName || machineName;

  return (
    <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-white/95 backdrop-blur-sm border-b border-gray-100 flex items-center gap-2 shadow-sm">
      <button
        onClick={onBack}
        className="p-1.5 hover:bg-gray-100 rounded-lg transition-all shrink-0"
        aria-label="Back"
      >
        <ArrowLeft className="w-4 h-4 text-gray-700" />
      </button>
      <div className="min-w-0 flex items-center gap-1 overflow-hidden">
        {/* Keyed by position, not value — a company and its area can share a name. */}
        {ancestors.map((seg, i) => (
          <span key={i} className="flex items-center gap-1 min-w-0">
            <span className="text-xs text-gray-400 font-normal truncate">{seg}</span>
            <span className="text-xs text-gray-300 shrink-0">&rsaquo;</span>
          </span>
        ))}
        <span className="text-sm text-gray-800 font-semibold shrink-0">{leaf}</span>
      </div>
      {statusColor && (
        <div className={`w-3 h-3 rounded-full shrink-0 ${statusColor}`}></div>
      )}
    </div>
  );
}
